import { Activity, Building2, Hotel, RadioTower, Smartphone, Workflow } from 'lucide-react';
import { CountUp } from '../../components/CountUp.tsx';
import { Reveal, Stagger, StaggerItem } from '../../components/motion.tsx';
import { COMMAND_STEPS, FEATURE_CARDS, PROOF_POINTS } from './landingData.ts';

const iconMap = {
  relay: RadioTower,
  portfolio: Building2,
  operator: Smartphone,
  hotel: Hotel,
};

interface Props {
  onContact: (subject: string) => void;
}

export function LandingSections({ onContact }: Props) {
  return (
    <>
      <Reveal>
        <div className="proof-strip">
          {PROOF_POINTS.map((point) => (
            <div className="proof" key={point.k}>
              <span>{point.k}</span>
              <strong>{point.v}</strong>
              <small>{point.note}</small>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal>
        <div className="section-head">
          <span className="num">01 /</span>
          <h2>
            Replace the back-office maze with a <span>live operating layer</span>
          </h2>
          <small>product surface</small>
        </div>
      </Reveal>

      <Stagger className="feature-grid">
        {FEATURE_CARDS.map((feature) => {
          const Icon = iconMap[feature.key as keyof typeof iconMap] ?? Activity;
          return (
            <StaggerItem className="feature-card" key={feature.key}>
              <div className="feature-icon"><Icon size={23} /></div>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </StaggerItem>
          );
        })}
      </Stagger>

      <Reveal>
        <section className="ops-panel">
          <div className="ops-copy">
            <span className="eyebrow">
              <span className="dot" />
              Relay to cloud in one path
            </span>
            <h2>
              The controller stays local. The operation becomes global.
            </h2>
            <p>
              ATRIUM keeps doing what it already does well at the door. CDVI Edge
              adds the missing SaaS layer around it: reliable relay transport,
              event ingestion, tenant isolation, and a polished operator console.
            </p>
          </div>

          <div className="command-path">
            {COMMAND_STEPS.map((step) => (
              <div className="path-step" key={step.id}>
                <span>{step.id}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="mission-cta">
          <div className="mission-meter">
            <Workflow size={28} />
            <strong>
              <CountUp value={182} duration={1300} /> doors
            </strong>
            <span>ready for one command surface</span>
          </div>
          <h2>
            Keep the hardware. Upgrade the entire experience.
          </h2>
          <p>
            Bring existing panels, cardholders, schedules, and sites. We turn the
            relay into a portfolio-grade access platform without rip-and-replace.
          </p>
          <div className="cta-row center">
            <a href="/handler/sign-in">
              <button className="btn-primary">Enter tenant</button>
            </a>
            <button className="btn-ghost" onClick={() => onContact('CDVI Edge - talk to us')}>
              Talk to us
            </button>
          </div>
        </section>
      </Reveal>
    </>
  );
}
